import { create } from 'zustand';
import { isEqual } from 'radash';
import {
  getResolvedObjectUpdateFields,
  markObjectUpdateFieldsResolved,
  markObjectUpdatesConsumed,
} from './consumedObjectUpdatesStore';

export type CredentialHeaderEntry = { key: string; value: string };
export type CredentialCookieEntry = { name: string; value: string };

export type CredentialAuthMaterialEditableField = 'headers' | 'cookies';

export type PendingCredentialAuthMaterialFieldEdit = {
  field: CredentialAuthMaterialEditableField;
  before: CredentialHeaderEntry[] | CredentialCookieEntry[];
  after: CredentialHeaderEntry[] | CredentialCookieEntry[];
  sourceUpdateId?: string;
};

export type PendingCredentialAuthMaterialEdit = {
  fields: PendingCredentialAuthMaterialFieldEdit[];
  reason?: string;
  timestamp: number;
};

type CredentialAuthMaterialDraftFields = {
  headers: CredentialHeaderEntry[];
  cookies: CredentialCookieEntry[];
};

const CREDENTIAL_AUTH_MATERIAL_EDITABLE_FIELD_KEYS: CredentialAuthMaterialEditableField[] =
  ['headers', 'cookies'];

function isCredentialAuthMaterialEditableField(
  key: string,
): key is CredentialAuthMaterialEditableField {
  return CREDENTIAL_AUTH_MATERIAL_EDITABLE_FIELD_KEYS.includes(
    key as CredentialAuthMaterialEditableField,
  );
}

function normalizeHeaders(value: unknown): CredentialHeaderEntry[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const headers = value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const key = (entry as { key?: unknown }).key;
    const headerValue = (entry as { value?: unknown }).value;
    if (typeof key !== 'string' || typeof headerValue !== 'string') {
      return [];
    }
    return [{ key, value: headerValue }];
  });

  return headers.length > 0 ? headers : undefined;
}

function normalizeCookies(value: unknown): CredentialCookieEntry[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const cookies = value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const name = (entry as { name?: unknown }).name;
    const cookieValue = (entry as { value?: unknown }).value;
    if (typeof name !== 'string' || typeof cookieValue !== 'string') {
      return [];
    }
    return [{ name, value: cookieValue }];
  });

  return cookies.length > 0 ? cookies : undefined;
}

function normalizePatch(
  patch: Record<string, unknown>,
): Partial<CredentialAuthMaterialDraftFields> {
  const normalized: Partial<CredentialAuthMaterialDraftFields> = {};
  const headers = normalizeHeaders(patch.headers);
  if (headers) {
    normalized.headers = headers;
  }
  const cookies = normalizeCookies(patch.cookies);
  if (cookies) {
    normalized.cookies = cookies;
  }
  return normalized;
}

function mergePendingFieldEdits({
  existing,
  patch,
  baseFields,
  sourceUpdateId,
}: {
  existing: PendingCredentialAuthMaterialFieldEdit[];
  patch: Partial<CredentialAuthMaterialDraftFields>;
  baseFields?: CredentialAuthMaterialDraftFields;
  sourceUpdateId?: string;
}): PendingCredentialAuthMaterialFieldEdit[] {
  const byField = new Map<
    CredentialAuthMaterialEditableField,
    PendingCredentialAuthMaterialFieldEdit
  >(existing.map((entry) => [entry.field, entry]));

  for (const [key, patchEntries] of Object.entries(patch)) {
    if (
      !isCredentialAuthMaterialEditableField(key) ||
      patchEntries === undefined
    ) {
      continue;
    }
    const before = baseFields?.[key] ?? [];
    const after =
      key === 'headers'
        ? mergeCredentialHeaders({
            existing: before as CredentialHeaderEntry[],
            patch: patchEntries as CredentialHeaderEntry[],
          })
        : mergeCredentialCookies({
            existing: before as CredentialCookieEntry[],
            patch: patchEntries as CredentialCookieEntry[],
          });
    if (isEqual(before, after)) {
      byField.delete(key);
      continue;
    }
    byField.set(key, {
      field: key,
      before,
      after,
      sourceUpdateId,
    });
  }

  return [...byField.values()];
}

function uniqueSourceUpdateIds(ids: Array<string | undefined>): string[] {
  return [
    ...new Set(
      ids.filter((id): id is string => typeof id === 'string' && id.length > 0),
    ),
  ];
}

function collectSourceUpdateIds(
  entries: PendingCredentialAuthMaterialFieldEdit[],
): string[] {
  return uniqueSourceUpdateIds(entries.map((entry) => entry.sourceUpdateId));
}

function filterFullyResolvedSourceUpdateIds({
  resolvedEntries,
  remainingEntries,
}: {
  resolvedEntries: PendingCredentialAuthMaterialFieldEdit[];
  remainingEntries: PendingCredentialAuthMaterialFieldEdit[];
}): string[] {
  const resolvedSourceUpdateIds = collectSourceUpdateIds(resolvedEntries);
  const remainingSourceUpdateIds = new Set(
    collectSourceUpdateIds(remainingEntries),
  );
  return resolvedSourceUpdateIds.filter(
    (sourceUpdateId) => !remainingSourceUpdateIds.has(sourceUpdateId),
  );
}

export function mergeCredentialHeaders({
  existing,
  patch,
}: {
  existing: CredentialHeaderEntry[];
  patch: CredentialHeaderEntry[];
}): CredentialHeaderEntry[] {
  const byKey = new Map(
    existing.map((header) => [header.key.toLowerCase(), header]),
  );
  for (const header of patch) {
    byKey.set(header.key.toLowerCase(), header);
  }
  return [...byKey.values()];
}

export function mergeCredentialCookies({
  existing,
  patch,
}: {
  existing: CredentialCookieEntry[];
  patch: CredentialCookieEntry[];
}): CredentialCookieEntry[] {
  const byName = new Map(
    existing.map((cookie) => [cookie.name.toLowerCase(), cookie]),
  );
  for (const cookie of patch) {
    byName.set(cookie.name.toLowerCase(), cookie);
  }
  return [...byName.values()];
}

type CredentialAuthMaterialEditStore = {
  draftFieldsByCredentialId: Record<string, CredentialAuthMaterialDraftFields>;
  pendingAiEdits: Record<string, PendingCredentialAuthMaterialEdit>;
  stageAiPatch: (
    credentialId: string,
    {
      patch,
      reason,
      baseFields,
      sourceUpdateId,
    }: {
      patch: Record<string, unknown>;
      reason?: string;
      baseFields?: CredentialAuthMaterialDraftFields;
      sourceUpdateId?: string;
    },
  ) => boolean;
  acceptAiEdits: (
    credentialId: string,
    fields?: CredentialAuthMaterialEditableField[],
    options?: { baseDraftFields: CredentialAuthMaterialDraftFields },
  ) => CredentialAuthMaterialDraftFields | undefined;
  discardAiEdits: (
    credentialId: string,
    fields?: CredentialAuthMaterialEditableField[],
  ) => void;
  getPendingAiEdits: (
    credentialId: string,
  ) => PendingCredentialAuthMaterialEdit | undefined;
  syncCredentialAuthMaterialEditBaseFields: (
    credentialId: string,
    baseFields: CredentialAuthMaterialDraftFields,
  ) => void;
  acceptAllPendingAiEdits: (
    baseFieldsByCredentialId: Record<string, CredentialAuthMaterialDraftFields>,
  ) => Record<string, CredentialAuthMaterialDraftFields>;
  discardAllPendingAiEdits: () => void;
  clearPendingEditsForSourceUpdateIds: (sourceUpdateIds: string[]) => void;
  clearAllPendingCredentialAuthMaterialEdits: () => void;
  clearCredentialAuthMaterialEditState: (credentialId: string) => void;
};

export const useCredentialAuthMaterialEditStore =
  create<CredentialAuthMaterialEditStore>((set, get) => ({
    draftFieldsByCredentialId: {},
    pendingAiEdits: {},

    stageAiPatch: (
      credentialId,
      { patch, reason, baseFields, sourceUpdateId },
    ) => {
      const normalizedPatch = normalizePatch(patch);
      if (sourceUpdateId) {
        const resolvedFields = getResolvedObjectUpdateFields(sourceUpdateId);
        for (const field of Object.keys(normalizedPatch)) {
          if (resolvedFields.has(field)) {
            delete normalizedPatch[
              field as CredentialAuthMaterialEditableField
            ];
          }
        }
      }

      if (Object.keys(normalizedPatch).length === 0) {
        return false;
      }

      const existingPending = get().pendingAiEdits[credentialId];
      const resolvedBaseFields =
        baseFields ?? get().draftFieldsByCredentialId[credentialId];
      const nextFields = mergePendingFieldEdits({
        existing: existingPending?.fields ?? [],
        patch: normalizedPatch,
        baseFields: resolvedBaseFields,
        sourceUpdateId,
      });

      if (nextFields.length === 0) {
        return false;
      }

      set((state) => ({
        pendingAiEdits: {
          ...state.pendingAiEdits,
          [credentialId]: {
            fields: nextFields,
            reason: reason ?? existingPending?.reason,
            timestamp: Date.now(),
          },
        },
        draftFieldsByCredentialId: resolvedBaseFields
          ? {
              ...state.draftFieldsByCredentialId,
              [credentialId]: resolvedBaseFields,
            }
          : state.draftFieldsByCredentialId,
      }));
      return true;
    },

    acceptAiEdits: (credentialId, fields, options) => {
      const pending = get().pendingAiEdits[credentialId];
      if (!pending || !options?.baseDraftFields) {
        return undefined;
      }

      const fieldsToAccept = fields
        ? pending.fields.filter((entry) => fields.includes(entry.field))
        : pending.fields;
      if (fieldsToAccept.length === 0) {
        return undefined;
      }

      const nextDraftFields = { ...options.baseDraftFields };
      for (const entry of fieldsToAccept) {
        if (entry.field === 'headers') {
          nextDraftFields.headers = entry.after as CredentialHeaderEntry[];
          continue;
        }
        nextDraftFields.cookies = entry.after as CredentialCookieEntry[];
      }

      const remainingFields = pending.fields.filter(
        (entry) =>
          !fieldsToAccept.some((accepted) => accepted.field === entry.field),
      );

      const sourceUpdateIds = uniqueSourceUpdateIds(
        fieldsToAccept.map((entry) => entry.sourceUpdateId),
      );
      if (sourceUpdateIds.length > 0) {
        markObjectUpdatesConsumed(sourceUpdateIds);
        for (const sourceUpdateId of sourceUpdateIds) {
          markObjectUpdateFieldsResolved({
            sourceUpdateId,
            fields: fieldsToAccept
              .filter((entry) => entry.sourceUpdateId === sourceUpdateId)
              .map((entry) => entry.field),
          });
        }
      }

      set((state) => {
        const nextPendingAiEdits = { ...state.pendingAiEdits };
        if (remainingFields.length === 0) {
          delete nextPendingAiEdits[credentialId];
        } else {
          nextPendingAiEdits[credentialId] = {
            ...pending,
            fields: remainingFields,
          };
        }

        return {
          pendingAiEdits: nextPendingAiEdits,
          draftFieldsByCredentialId: {
            ...state.draftFieldsByCredentialId,
            [credentialId]: nextDraftFields,
          },
        };
      });

      return nextDraftFields;
    },

    discardAiEdits: (credentialId, fields) => {
      const pending = get().pendingAiEdits[credentialId];
      if (!pending) {
        return;
      }

      const discardedEntries = fields
        ? pending.fields.filter((entry) => fields.includes(entry.field))
        : pending.fields;

      const remainingFields = fields
        ? pending.fields.filter((entry) => !fields.includes(entry.field))
        : [];

      const consumedSourceUpdateIds = filterFullyResolvedSourceUpdateIds({
        resolvedEntries: discardedEntries,
        remainingEntries: remainingFields,
      });
      if (consumedSourceUpdateIds.length > 0) {
        markObjectUpdatesConsumed(consumedSourceUpdateIds);
      }

      set((state) => {
        const nextPendingAiEdits = { ...state.pendingAiEdits };
        if (!fields || remainingFields.length === 0) {
          delete nextPendingAiEdits[credentialId];
        } else {
          nextPendingAiEdits[credentialId] = {
            ...pending,
            fields: remainingFields,
          };
        }
        return { pendingAiEdits: nextPendingAiEdits };
      });
    },

    getPendingAiEdits: (credentialId) => get().pendingAiEdits[credentialId],

    syncCredentialAuthMaterialEditBaseFields: (credentialId, baseFields) => {
      const pending = get().pendingAiEdits[credentialId];
      const existingDraft = get().draftFieldsByCredentialId[credentialId];
      const draftUnchanged =
        isEqual(existingDraft?.headers, baseFields.headers) &&
        isEqual(existingDraft?.cookies, baseFields.cookies);

      if (!pending) {
        if (draftUnchanged || existingDraft) {
          return;
        }
        set((state) => ({
          draftFieldsByCredentialId: {
            ...state.draftFieldsByCredentialId,
            [credentialId]: baseFields,
          },
        }));
        return;
      }

      const refreshedFields = pending.fields
        .map((entry) => ({
          ...entry,
          before: baseFields[entry.field],
        }))
        .filter((entry) => !isEqual(entry.before, entry.after));
      const pendingUnchanged =
        refreshedFields.length === pending.fields.length &&
        refreshedFields.every(
          (entry, index) =>
            isEqual(entry.before, pending.fields[index]?.before) &&
            isEqual(entry.after, pending.fields[index]?.after) &&
            entry.field === pending.fields[index]?.field,
        );

      if (pendingUnchanged && draftUnchanged) {
        return;
      }

      set((state) => {
        const nextPendingAiEdits = { ...state.pendingAiEdits };
        if (refreshedFields.length === 0) {
          delete nextPendingAiEdits[credentialId];
        } else {
          nextPendingAiEdits[credentialId] = {
            ...pending,
            fields: refreshedFields,
          };
        }

        return {
          pendingAiEdits: nextPendingAiEdits,
          draftFieldsByCredentialId: {
            ...state.draftFieldsByCredentialId,
            [credentialId]: baseFields,
          },
        };
      });
    },

    acceptAllPendingAiEdits: (baseFieldsByCredentialId) => {
      const acceptedByCredentialId: Record<
        string,
        CredentialAuthMaterialDraftFields
      > = {};
      const credentialIds = Object.keys(get().pendingAiEdits).filter(
        (credentialId) =>
          (get().pendingAiEdits[credentialId]?.fields.length ?? 0) > 0,
      );

      for (const credentialId of credentialIds) {
        const baseDraftFields = baseFieldsByCredentialId[credentialId];
        if (!baseDraftFields) {
          continue;
        }
        const nextFields = get().acceptAiEdits(credentialId, undefined, {
          baseDraftFields,
        });
        if (nextFields) {
          acceptedByCredentialId[credentialId] = nextFields;
        }
      }

      return acceptedByCredentialId;
    },

    discardAllPendingAiEdits: () => {
      const consumedSourceUpdateIds = uniqueSourceUpdateIds(
        Object.values(get().pendingAiEdits).flatMap((pending) =>
          pending.fields.map((entry) => entry.sourceUpdateId),
        ),
      );
      if (consumedSourceUpdateIds.length > 0) {
        markObjectUpdatesConsumed(consumedSourceUpdateIds);
      }
      set({ pendingAiEdits: {} });
    },

    clearPendingEditsForSourceUpdateIds: (sourceUpdateIds) => {
      if (sourceUpdateIds.length === 0) {
        return;
      }

      const idsToClear = new Set(sourceUpdateIds);
      set((state) => {
        const nextPendingAiEdits = { ...state.pendingAiEdits };
        let changed = false;

        for (const [credentialId, pending] of Object.entries(
          nextPendingAiEdits,
        )) {
          const remainingFields = pending.fields.filter(
            (entry) =>
              !entry.sourceUpdateId || !idsToClear.has(entry.sourceUpdateId),
          );
          if (remainingFields.length === pending.fields.length) {
            continue;
          }

          changed = true;
          if (remainingFields.length === 0) {
            delete nextPendingAiEdits[credentialId];
          } else {
            nextPendingAiEdits[credentialId] = {
              ...pending,
              fields: remainingFields,
            };
          }
        }

        return changed ? { pendingAiEdits: nextPendingAiEdits } : state;
      });
    },

    clearAllPendingCredentialAuthMaterialEdits: () => {
      set({ pendingAiEdits: {} });
    },

    clearCredentialAuthMaterialEditState: (credentialId) => {
      set((state) => {
        const { [credentialId]: _draft, ...draftFieldsByCredentialId } =
          state.draftFieldsByCredentialId;
        const { [credentialId]: _pending, ...pendingAiEdits } =
          state.pendingAiEdits;
        return { draftFieldsByCredentialId, pendingAiEdits };
      });
    },
  }));
